import React, { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ArticlePage from './ArticlePage';
import ShareButton from './ShareButton';
import { trackEvent } from '../utils/helpers';
import './ArticleModal.css';

const ArticleModal = ({ isOpen, onClose, article, isDirectVisit = false }) => {
  const [shouldRender, setShouldRender] = useState(false);
  const navigate = useNavigate();

  // Handle rendering and transition timing
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
    } else {
      // Delay unmounting to allow closing animation
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300); // Match transition duration
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle close with appropriate navigation
  const handleClose = useCallback(() => {
    trackEvent('article-modal-closed', {
      articleId: article?.id,
      isDirectVisit,
      method: 'programmatic'
    });
    onClose();
    // Small delay to allow transition to start before navigation
    setTimeout(() => {
      if (isDirectVisit) {
        // For direct visits, navigate to homepage
        navigate('/', { replace: true });
      } else {
        // For normal modal opens, use browser history
        window.history.back();
      }
    }, 50);
  }, [onClose, isDirectVisit, navigate, article]);

  // Handle ESC key to close modal
  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Escape' && isOpen) {
      trackEvent('article-modal-closed', {
        articleId: article?.id,
        method: 'escape'
      });
      handleClose();
    }
  }, [isOpen, handleClose, article]);

  // Handle modal effects
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  // Handle backdrop click to close modal
  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      trackEvent('article-modal-closed', {
        articleId: article?.id,
        method: 'backdrop'
      });
      handleClose();
    }
  };

  // Handle close button click
  const handleCloseClick = () => {
    trackEvent('article-modal-closed', {
      articleId: article?.id,
      method: 'button'
    });
    handleClose();
  };

  if (!shouldRender) return null;

  return (
    <div className={`article-modal ${isOpen ? 'visible' : ''}`}>
      <div className="article-modal-backdrop" onClick={handleBackdropClick}>
        <div className="article-modal-container">
          <header className="article-modal-header">
            <div className="header-content">
              <button onClick={handleCloseClick} className="header-back-button" aria-label="Tillbaka">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 16L6 10L12 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <div className="site-title">
                Spanska Nyheter
              </div>
              <div className="header-actions">
                <ShareButton />
              </div>
            </div>
          </header>
          <div className="article-modal-content">
            <ArticlePage articleProp={article} isModal={true} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleModal;
